import { SerializedData, Store } from 'bernie-core';
import { Logger } from 'bernie-logger';
import { PzCampaignSource } from 'sources/pz-campaign-source';
import { action, makeObservable, observable } from 'mobx';
import { PzCampaign } from 'models';

export class PzCampaignStore extends Store {
  public campaign: PzCampaign;
  public campaignTerms: string;
  public campaignTermsId: string;
  public campaignTermsLocale: string;
  public campaignState = 'pending';

  private readonly DEFAULT_LOCALE: string = 'en_us';

  private readonly pzCampaignSource: PzCampaignSource;

  public constructor(pzCampaignSource: PzCampaignSource, state: SerializedData = {}, logger: Logger) {
    super(state, logger);
    this.pzCampaignSource = pzCampaignSource;

    makeObservable(this, {
      campaign: observable,
      campaignTerms: observable,
      campaignTermsId: observable,
      campaignTermsLocale: observable,
      campaignState: observable,
      campaignFetchSuccess: action,
      campaignFetchFailure: action,
    });
  }

  public hydrate(data: SerializedData): void {
    if (data.pzCampaignSource) {
      Object.assign(this.pzCampaignSource, data.pzsource);
    }

    delete data.pzCampaignSource;
    Object.assign(this, data);
  }

  public async fetchCampaign(programName: string, locale: string, defaultLocale: string = this.DEFAULT_LOCALE) {
    this.campaignState = 'pending';
    this.campaignTermsId = null;

    try {
      const newCampaign = await this.pzCampaignSource.getCampaign(programName);
      this.campaignFetchSuccess(newCampaign, locale.toLowerCase(), defaultLocale);
    } catch (e) {
      this.campaignFetchFailure(e);
    }
  }

  public campaignFetchSuccess = (campaign: PzCampaign, locale: string, defaultLocale: string = this.DEFAULT_LOCALE) => {
    this.campaign = campaign;
    this.campaignTermsLocale = locale;

    /*
    take locale and return a list of compatible locales.
    if the first doesn't exist, try the others.
    fallback to the default locale and try it all again
    */

    if (campaign.terms) {
      const localeTerms = this.findTermsForLocale(campaign.terms, locale);
      if (localeTerms) {
        this.campaignTermsId = localeTerms.campaignTermsId;
        this.campaignTerms = localeTerms.campaignTerms;
      } else {
        const defaultLocaleTerms = this.findTermsForLocale(campaign.terms, defaultLocale);
        if (defaultLocaleTerms) {
          this.campaignTermsId = defaultLocaleTerms.campaignTermsId;
          this.campaignTerms = defaultLocaleTerms.campaignTerms;
        }
      }

      this.campaignState = 'done';
    }
  };

  public campaignFetchFailure(error) {
    this.campaignState = 'error';
  }

  private findTermsForLocale(campaignTerms, locale: string) {
    const localeChains = [
      ['en_au', 'en_nz', 'en_ca', 'en', 'en_us'],
      ['ca_fr', 'fr'],
    ];

    const localeChain = localeChains.filter((chain) => chain.includes(locale))[0] || [locale];

    for (const l of localeChain) {
      if (campaignTerms[l]) {
        return {
          campaignTermsId: campaignTerms[l].terms_id,
          campaignTerms: campaignTerms[l].terms,
        };
      }
    }

    return null;
  }
}
